'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission, guardedClient as request } from '@repo/shared/helpers';
import EventsTab from 'src/parts/tabs/EventsTab';
import { getUserEvents } from 'src/api/admin/events';
import { getEventTypes } from 'src/api/admin/types';
import { getAllTags } from 'src/api/admin/tags';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';

export const Route = createFileRoute('/_admin/users/$userId/events')({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			page: (search.page as number) || undefined,
			sort: (search.sort as string) || undefined,
		};
	},
	loaderDeps: ({ search }) => search,
	loader: async ({ params, deps }) => {
		const { userId } = params;
		const [events, types, tags] = await Promise.all([getUserEvents(userId, deps), getEventTypes(), getAllTags()]);
		return { events, types, tags };
	},
	component: EventsTabPage,
});

function EventsTabPage() {
	const { userId } = Route.useParams();
	const searchParams = Route.useSearch();
	const { events: initialEvents, types, tags } = Route.useLoaderData();
	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const { config } = useConfig();
	const featureFlags = config?.featureFlags || {};
	const { data: events, refetch: refetchEvents } = useQuery({
		queryKey: ['user-events', userId, searchParams],
		queryFn: () => getUserEvents(userId, searchParams),
		initialData: initialEvents,
	});

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const handleReload = () => {
		refetchEvents();
	};

	const addEvent = async (data: Record<string, unknown>) => {
		await request.post('/api/events', data);
	};

	return (
		<EventsTab
			authenticatedUser={authenticatedUser}
			events={events}
			addEvent={addEvent}
			permissions={{
				view: verifyPermission(PERMISSIONS.adminEventsView),
				create: verifyPermission(PERMISSIONS.adminEventsCreate),
				update: verifyPermission(PERMISSIONS.adminEventsEdit),
				archive: verifyPermission(PERMISSIONS.adminEventsArchive),
			}}
			onReload={handleReload}
			types={types.data || []}
			userId={userId}
			singleGroupAsOwner={featureFlags?.USES_SUBCONTRACTING || false}
			featureFlags={featureFlags}
			tags={tags || []}
		/>
	);
}
